import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Container, Typography, Box, Card, CardContent, Button, Grid,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, Alert, CircularProgress,
    LinearProgress, IconButton, Tooltip, TableSortLabel, alpha
} from '@mui/material';
import {
    Business, Description, Add, CloudDownload,
    CheckCircle, Schedule, Settings,
    ArrowUpward, ArrowDownward
} from '@mui/icons-material';
import axios from 'axios';
import io from 'socket.io-client';

const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_SERVER_URL || "http://localhost:3001";
const SYNC_TIMEOUT = 10 * 60 * 1000; // 10 minutes timeout

const Dashboard = () => {
    const [stats, setStats] = useState({});
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncResults, setSyncResults] = useState(null);
    const [syncingAll, setSyncingAll] = useState(false);
    const [overallProgress, setOverallProgress] = useState({ value: 0, label: '' });
    const [clientsProgress, setClientsProgress] = useState({});
    const [cronStatus, setCronStatus] = useState(null);
    const [websocketConnected, setWebsocketConnected] = useState(false);
    const [syncingClient, setSyncingClient] = useState(null);
    
    // État pour le tri
    const [sortConfig, setSortConfig] = useState({
        field: 'codeClient',
        direction: 'asc'
    });

    const isSyncingRef = useRef(false);
    const socketRef = useRef();
    const syncResultsTimeoutRef = useRef(null);
    const syncTimeoutRef = useRef(null);

    // Palette unifiée
    const palette = useMemo(
        () => ({
            navy: '#0B1E3C',
            navy2: '#102B55',
            teal: '#18B6B0',
            teal2: '#39D0C9',
            tealLight: '#E0F7F6',
            green: '#7BC043',
            gold: '#F3C15C',
            paper: '#FFFFFF',
            gradient: 'linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)',
            shadow: '0 10px 40px rgba(11, 30, 60, 0.08)',
            textPrimary: '#1E293B',
            textSecondary: '#64748B'
        }),
        []
    );

    // Fonction de tri
    const sortedClients = useMemo(() => {
        const sortableClients = [...clients];
        
        sortableClients.sort((a, b) => {
            let aValue = a[sortConfig.field];
            let bValue = b[sortConfig.field];
            
            // Traitement spécial pour le code client (tri numérique)
            if (sortConfig.field === 'codeClient') {
                aValue = parseInt(aValue) || 0;
                bValue = parseInt(bValue) || 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        
        return sortableClients;
    }, [clients, sortConfig]);

    const handleSort = (field) => {
        let direction = 'asc';
        
        if (sortConfig.field === field && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        setSortConfig({ field, direction });
    };

    useEffect(() => {
        initializePage();
        if (Notification.permission === 'default') Notification.requestPermission();
        setupWebSocket();

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current);
            }
        };
    }, []);

    // Charger le statut du cron serveur
    useEffect(() => {
        const fetchCronStatus = async () => {
            try {
                const res = await axios.get('/api/cron/status');
                setCronStatus(res.data);
            } catch {}
        };
        fetchCronStatus();
        const interval = setInterval(fetchCronStatus, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (syncResults) {
            if (syncResultsTimeoutRef.current) {
                clearTimeout(syncResultsTimeoutRef.current);
            }

            syncResultsTimeoutRef.current = setTimeout(() => {
                setSyncResults(null);
            }, 8000);

            return () => {
                if (syncResultsTimeoutRef.current) {
                    clearTimeout(syncResultsTimeoutRef.current);
                }
            };
        }
    }, [syncResults]);

    const initializePage = async () => {
        await loadData();
    };

    const loadData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Token d\'authentification manquant');
                setSyncResults({
                    success: false,
                    error: 'Session expirée - Veuillez vous reconnecter'
                });
                return;
            }
    
            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000 
            };
    
            let statsResponse;
            try {
                statsResponse = await axios.get('/api/comptable/dashboard', config);
            } catch (statsError) {
                console.error('❌ Erreur stats:', {
                    message: statsError.message,
                    status: statsError.response?.status,
                    data: statsError.response?.data,
                    url: statsError.config?.url
                });
                throw new Error(`Erreur statistiques: ${statsError.response?.data?.message || statsError.message}`);
            }
    
            let clientsResponse;
            try {
                clientsResponse = await axios.get('/api/comptable/clients', config);
            } catch (clientsError) {
                console.error('❌ Erreur clients:', {
                    message: clientsError.message,
                    status: clientsError.response?.status,
                    data: clientsError.response?.data,
                    url: clientsError.config?.url
                });
                throw new Error(`Erreur clients: ${clientsError.response?.data?.message || clientsError.message}`);
            }
    
            if (!statsResponse?.data) {
                console.warn('Data statistiques vides ou invalides');
                setStats({});
            } else {
                setStats(statsResponse.data);
            }
    
            if (!Array.isArray(clientsResponse?.data)) {
                console.warn('Données clients invalides, attendu un array:', clientsResponse?.data);
                setClients([]);
            } else {
                setClients(clientsResponse.data);
            }
    
    
        } catch (error) {
            console.error('Erreur globale chargement données:', {
                name: error.name,
                message: error.message,
                stack: error.stack,
                response: error.response
            });
    
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                setSyncResults({
                    success: false,
                    error: 'Session expirée - Veuillez vous reconnecter'
                });
               // todo redirection to login 
                return;
            }
    
            if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
                setSyncResults({
                    success: false,
                    error: 'Erreur de connexion au serveur'
                });
                return;
            }
    
            const errorMessage = error.message || 'Erreur inconnue lors du chargement des données';
            setSyncResults({
                success: false,
                error: errorMessage
            });
            
        } finally {
            setLoading(false);
        }
    };


    const setupWebSocket = () => {        
        try {
            socketRef.current = io(SOCKET_SERVER_URL);
            const socket = socketRef.current;

            socket.on('connect', () => {
                setWebsocketConnected(true);
                
                const token = localStorage.getItem('token');
                if (token) {
                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        socket.emit('join_room', payload.userId);
                    } catch (e) {
                        console.error("Erreur décodage token:", e);
                    }
                }
            });

            socket.on('connect_error', (err) => {
                setWebsocketConnected(false);
            });

            socket.on('disconnect', () => {
                setWebsocketConnected(false);
            });

            socket.on('sync-start', (data) => {
                setSyncingAll(true);
                isSyncingRef.current = true;
                setOverallProgress({ value: 0, label: data.message || 'Synchronisation démarrée...' });
                setClientsProgress({});
                
                if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
                syncTimeoutRef.current = setTimeout(() => {
                    console.warn('⚠️ Timeout de synchronisation atteint');
                    handleSyncTimeout();
                }, SYNC_TIMEOUT);
            });

            socket.on('sync-overall-progress', (data) => {
                const percent = (data.current / data.total) * 100;
                setOverallProgress({
                    value: percent,
                    label: `Client ${data.current}/${data.total}: ${data.clientName}`
                });
            });

            socket.on('sync-client-progress', (data) => {
                console.log('📈 Progression client:', data);
                setClientsProgress(prev => ({
                    ...prev,
                    [data.clientId]: { 
                        step: data.step, 
                        progress: data.progress || 0, 
                        error: null,
                        clientName: data.clientName
                    }
                }));
            });

            socket.on('sync-client-complete', (data) => {
                setClientsProgress(prev => ({
                    ...prev,
                    [data.clientId]: { 
                        step: `Terminé: ${data.newDocuments} nouveaux documents`, 
                        progress: 100, 
                        error: null,
                        clientName: data.clientName,
                        completed: true
                    }
                }));
            });

            socket.on('sync-client-error', (data) => {
                setClientsProgress(prev => ({
                    ...prev,
                    [data.clientId]: { 
                        ...prev[data.clientId], 
                        error: data.error,
                        step: 'Erreur de synchronisation'
                    }
                }));
            });

            socket.on('sync-complete', (data) => {
                handleSyncComplete(data);
            });

        } catch (error) {
            console.error('❌ Erreur configuration WebSocket:', error);
            setWebsocketConnected(false);
        }
    };

    const handleSyncComplete = async (data) => {
    
        if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
            syncTimeoutRef.current = null;
        }
    
        if (!data || typeof data !== 'object') {
            console.error('❌ Données sync-complete invalides:', data);
            data = {
                success: false,
                error: 'Données de synchronisation invalides',
                totalNewDocuments: 0
            };
        }
    
        isSyncingRef.current = false;
        setSyncingAll(false);
        setOverallProgress({ 
            value: 100, 
            label: data.message || 'Synchronisation terminée' 
        });
        
        if (data.success !== false) {
            // Rafraîchir le statut du cron après une sync réussie
            try {
                const res = await axios.get('/api/cron/status');
                setCronStatus(res.data);
            } catch {}
        }
        
        setSyncResults({
            success: data.success !== false, 
            totalNewDocuments: data.totalNewDocuments || 0,
            message: data.message,
            error: data.error
        });
        
        setTimeout(() => {
            loadData();
        }, 2000);
        
        setTimeout(() => {
            setOverallProgress({ value: 0, label: '' });
            setClientsProgress({});
        }, 5000); 
    
        if (Notification.permission === 'granted' && data.success !== false) {
            new Notification('Synchronisation terminée', {
                body: `${data.totalNewDocuments || 0} nouveaux documents récupérés`,
                icon: '/favicon.ico'
            });
        }
    };

    const handleSyncTimeout = () => {
        isSyncingRef.current = false;
        setSyncingAll(false);
        setOverallProgress({ value: 0, label: '' });
        setClientsProgress({});
        
        setSyncResults({
            success: false,
            error: 'Synchronisation interrompue (timeout). Veuillez réessayer.'
        });

        if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
            syncTimeoutRef.current = null;
        }
    };

    const handleSyncAll = async (isAutoSync = false) => {
        if (isSyncingRef.current) {
            return;
        }
            
        if (!websocketConnected) {
            console.warn('⚠️ WebSocket non connecté - mode fallback');
            setSyncingAll(true);
            isSyncingRef.current = true;
            setOverallProgress({ 
                value: 0, 
                label: isAutoSync ? 'Synchronisation automatique (mode offline)...' : 'Synchronisation en cours...' 
            });
        }

        try {
            const response = await axios.post('/api/tej/sync-all');
            
            if (!websocketConnected) {
                setTimeout(() => {
                    handleSyncComplete({
                        success: true,
                        message: 'Synchronisation terminée (mode offline)',
                        totalNewDocuments: 0
                    });
                }, 30000); 
            }

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
                        isSyncingRef.current = false;
            setSyncingAll(false);
            setOverallProgress({ value: 0, label: '' });
            
            setSyncResults({
                success: false,
                error: errorMessage
            });
        }
    };

    const handleSyncClient = async (clientId) => {
        if (syncingClient === clientId) return;
        
        setSyncingClient(clientId);
        setClientsProgress(prev => ({
            ...prev,
            [clientId]: { step: 'Démarrage...', progress: 0, error: null }
        }));

        try {
            const response = await axios.post(`/api/tej/sync/${clientId}`);
            
            setSyncResults({
                success: true,
                message: `Client synchronisé: ${response.data.data?.newDocuments || 0} nouveaux documents`
            });

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            
            setClientsProgress(prev => ({
                ...prev,
                [clientId]: { ...prev[clientId], error: errorMessage, step: 'Erreur' }
            }));

            setSyncResults({
                success: false,
                error: errorMessage
            });
        } finally {
            setSyncingClient(null);
            setTimeout(() => {
                setClientsProgress(prev => {
                    const updated = { ...prev };
                    delete updated[clientId];
                    return updated;
                });
            }, 5000);
        }
    };

    const formatNextCronSync = () => {
        if (!cronStatus?.nextRun) return null;

        const now = new Date();
        const nextRun = new Date(cronStatus.nextRun);
        const diffMs = nextRun.getTime() - now.getTime();

        if (diffMs <= 0) {
            if (cronStatus.isRunning) return "Synchronisation automatique en cours...";
            return "Synchronisation automatique imminente...";
        }

        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        let timeStr;
        if (diffHours > 0) {
            timeStr = `${diffHours}h ${diffMinutes}m`;
        } else if (diffMinutes > 0) {
            timeStr = `${diffMinutes}m`;
        } else {
            timeStr = "moins d'1 minute";
        }

        return `Prochaine sync auto dans ${timeStr} (Lun-Sam, 8h-18h)`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Chargement des données...</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: palette.gradient,
                color: palette.textPrimary,
                py: 4,
                px: 2
            }}
        >
            <Container maxWidth="lg">
                {!websocketConnected && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        🔌 WebSocket déconnecté - Les mises à jour en temps réel ne sont pas disponibles
                    </Alert>
                )}

                {syncResults && (
                    <Alert 
                        severity={syncResults.success ? 'success' : 'error'} 
                        sx={{ mb: 3 }} 
                        onClose={() => setSyncResults(null)}
                    >
                        {syncResults.success
                            ? `✅ ${syncResults.message || `Synchronisation réussie. ${syncResults.totalNewDocuments || 0} nouveaux documents.`}`
                            : `❌ Erreur: ${syncResults.error}`}
                    </Alert>
                )}

                {/* Barre de progression globale */}
                {overallProgress.value > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {overallProgress.label}
                                    {!websocketConnected && " (mode offline)"}
                                </Typography>
                                <LinearProgress variant="determinate" value={overallProgress.value} />
                                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    {Math.round(overallProgress.value)}%
                                </Typography>
                            </Box>
                        </Alert>
                    </Box>
                )}

                {/* Timer de sync automatique (géré par le serveur) */}
                {cronStatus && !syncingAll && formatNextCronSync() && (
                    <Alert severity="info" sx={{ mb: 3 }} icon={<Schedule />}>
                        {formatNextCronSync()}
                    </Alert>
                )}

                {/* Statistiques */}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                                transition: 'all .2s',
                                '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' }
                            }}
                        >
                            <CardContent sx={{ py: 3 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Business color="primary" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h3" sx={{ lineHeight: 1, fontWeight: 700 }}>
                                            {stats.clients || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">Clients</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                                transition: 'all .2s',
                                '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' }
                            }}
                        >
                            <CardContent sx={{ py: 3 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Description color="secondary" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h3" sx={{ lineHeight: 1, fontWeight: 700 }}>
                                            {stats.documents || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">Documents</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                                transition: 'all .2s',
                                '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' }
                            }}
                        >
                            <CardContent sx={{ py: 3 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Add color="success" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h3" sx={{ lineHeight: 1, fontWeight: 700 }}>
                                            {stats.documentsThisMonth || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">Documents TEJ Ce mois</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                                transition: 'all .2s',
                                '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' }
                            }}
                        >
                            <CardContent sx={{ py: 3 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Add color="success" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h3" sx={{ lineHeight: 1, fontWeight: 700 }}>
                                            {stats.documentsToday || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">Documents TEJ Aujourd'hui</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.06)'
                            }}
                        >
                            <CardContent>
                                <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" gap={1.25}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => handleSyncAll(false)}
                                        disabled={syncingAll || clients.length === 0}
                                        startIcon={syncingAll ? <CircularProgress size={20} /> : <CloudDownload />}
                                        sx={{ 
                                            py: 1.25, 
                                            borderRadius: 2,
                                            background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`,
                                            fontWeight: 700,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)`,
                                            }
                                        }}
                                    >
                                        {syncingAll ? 'Synchro...' : 'Sync Tous'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Table clients */}
                <Card
                    elevation={0}
                    sx={{ 
                        borderRadius: 4,
                        overflow: 'hidden',
                        bgcolor: 'white',
                        boxShadow: palette.shadow,
                        border: `1px solid ${alpha(palette.teal, 0.1)}`
                    }}
                >
                    <Box
                        sx={{
                            py: 3,
                            px: 4,
                            background: `linear-gradient(135deg, ${palette.navy} 0%, rgba(20,45,80,0.85) 100%)`,
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Clients TEJ ({sortedClients.length})
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Gérez et synchronisez vos clients
                            </Typography>
                        </Box>
                    </Box>
                    
                    <CardContent>
                        {sortedClients.length > 0 ? (
                            <TableContainer 
                                component={Paper} 
                                elevation={0}
                                sx={{ 
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(palette.teal, 0.1)}`,
                                    overflow: 'hidden',
                                    background: 'transparent'
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ 
                                            backgroundColor: alpha(palette.navy, 0.05),
                                            borderBottom: `2px solid ${alpha(palette.teal, 0.2)}`
                                        }}>
                                            <TableCell sx={{ py: 2.5 }}>
                                                <TableSortLabel
                                                    active={sortConfig.field === 'codeClient'}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort('codeClient')}
                                                    sx={{ fontWeight: 700, color: palette.textPrimary }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        Code Client
                                                        {sortConfig.field === 'codeClient' && (
                                                            sortConfig.direction === 'asc' ? 
                                                            <ArrowUpward fontSize="small" /> : 
                                                            <ArrowDownward fontSize="small" />
                                                        )}
                                                    </Box>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Nom
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Type
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Email
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Statut
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.textPrimary }}>
                                                    Actions
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedClients.map((client) => {
                                            const progress = clientsProgress[client._id];
                                            const isClientSyncing = syncingClient === client._id;
                                            
                                            return (
                                                <TableRow 
                                                    key={client._id}
                                                    sx={{ 
                                                        '&:hover': {
                                                            backgroundColor: alpha(palette.tealLight, 0.5),
                                                        },
                                                        '&:nth-of-type(even)': {
                                                            backgroundColor: alpha(palette.tealLight, 0.1),
                                                        },
                                                        borderBottom: `1px solid ${alpha(palette.teal, 0.05)}`
                                                    }}
                                                >
                                                    <TableCell sx={{ py: 2.5 }}>
                                                        <Chip 
                                                            label={client.codeClient} 
                                                            size="small"
                                                            sx={{ 
                                                                backgroundColor: alpha(palette.teal, 0.1),
                                                                color: palette.teal,
                                                                fontWeight: 700,
                                                                borderRadius: 2,
                                                                border: `1px solid ${alpha(palette.teal, 0.2)}`
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2.5 }}>
                                                        <Typography sx={{ fontWeight: 600, color: palette.textPrimary }}>
                                                            {client.raisonSociale || `${client.prenom} ${client.nom}`}
                                                        </Typography>
                                                        {progress && (
                                                            <Box sx={{ width: '100%', mt: 1 }}>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={progress.progress || 0}
                                                                    color={progress.error ? 'error' : progress.completed ? 'success' : 'primary'}
                                                                />
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color={progress.error ? 'error' : 'text.secondary'}
                                                                    sx={{ display: 'block', mt: 0.5 }}
                                                                >
                                                                    {progress.error ? `❌ ${progress.error}` : 
                                                                    progress.completed ? `✅ ${progress.step}` : 
                                                                    `🔄 ${progress.step}`}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2.5 }}>
                                                        <Chip 
                                                            label={client.typePersonne} 
                                                            size="small"
                                                            sx={{ 
                                                                backgroundColor: alpha(palette.green, 0.1),
                                                                color: palette.green,
                                                                fontWeight: 600,
                                                                borderRadius: 2,
                                                                border: `1px solid ${alpha(palette.green, 0.2)}`
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2.5 }}>
                                                        <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                                                            {client.email}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2.5 }}>
                                                        <Chip 
                                                            label="Actif" 
                                                            size="small" 
                                                            icon={<CheckCircle />}
                                                            sx={{ 
                                                                backgroundColor: alpha('#4caf50', 0.1),
                                                                color: '#4caf50',
                                                                fontWeight: 600,
                                                                borderRadius: 2,
                                                                border: `1px solid ${alpha('#4caf50', 0.2)}`
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2.5 }}>
                                                        <Box display="flex" gap={1}>
                                                            <Tooltip title="Synchroniser ce client">
                                                                <span>
                                                                    <IconButton
                                                                        onClick={() => handleSyncClient(client._id)}
                                                                        disabled={isClientSyncing || syncingAll}
                                                                        size="small"
                                                                        sx={{ 
                                                                            backgroundColor: alpha(palette.teal, 0.1),
                                                                            color: palette.teal,
                                                                            '&:hover': {
                                                                                backgroundColor: alpha(palette.teal, 0.2),
                                                                                transform: 'translateY(-2px)',
                                                                                boxShadow: `0 4px 12px ${alpha(palette.teal, 0.3)}`
                                                                            },
                                                                            transition: 'all 0.3s'
                                                                        }}
                                                                    >
                                                                        {isClientSyncing ? 
                                                                            <CircularProgress size={20} /> : 
                                                                            <CloudDownload />
                                                                        }
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Tester la connexion">
                                                                <IconButton
                                                                    onClick={() => console.log('Test connexion', client._id)}
                                                                    size="small"
                                                                    sx={{ 
                                                                        backgroundColor: alpha(palette.navy, 0.1),
                                                                        color: palette.navy,
                                                                        '&:hover': {
                                                                            backgroundColor: alpha(palette.navy, 0.2),
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: `0 4px 12px ${alpha(palette.navy, 0.3)}`
                                                                        },
                                                                        transition: 'all 0.3s'
                                                                    }}
                                                                >
                                                                    <Settings />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert severity="info">
                                Aucun client trouvé. Ajoutez des clients pour commencer la synchronisation.
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default Dashboard;